import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { AuthService } from '../app/features/auth/services/auth.service'; 
import { environment } from '../environments/environment';

// Interface pour la pagination
interface Pageable {
  pageNumber: number;
  pageSize: number;
  sort: {
    unsorted: boolean;
    sorted: boolean;
    empty: boolean;
  };
  offset: number;
  unpaged: boolean;
  paged: boolean;
}

// Interface pour les autorités
interface Authority {
  authority: string;
}

// Interface pour le plan de subscription
interface SubscriptionPlan {
  id: number;
  name: string;
  totalCost: number;
  installmentCount: number;
}

// Interface pour la subscription
interface Subscription {
  id: number;
  user: string;
  subscriptionPlan: SubscriptionPlan;
  startDate: string;
  endDate: string;
  active: boolean;
  paidAmount: number;
  installmentCount: number;
  dateInvoice: string;
  status: string;
  renewed: boolean;
}

// Interface pour la company
interface Company {
  id: number;
  name: string;
  logo: string;
  primaryColor: string;
  secondaryColor: string;
}

// Interface pour l'utilisateur/worker
export interface Worker {
  id: number;
  nom: string;
  prenom: string;
  email: string;
  password: string;
  adress: string;
  technicalSheet: string | null;
  profil: string;
  activated: boolean;
  notifiable: boolean;
  telephone: string;
  present: boolean;
  subscriptions: Subscription[];
  company: Company | null;
  createdAt: string | number[];
  funds: number;
  note: number;
  photo: string | null;
  idCard: string | null;
  dateOfBirth?: string | number[];
  lieunaissance?: string;
  qrcode?: string | null;
  accountNonExpired: boolean;
  credentialsNonExpired: boolean;
  accountNonLocked: boolean;
  username: string;
  authorities: Authority[];
  enabled: boolean;
}

// Champs multipart/form-data attendus par PUT /api/v1/user/update/{id}
export interface UpdateWorkerFormData {
  nom?: string;
  prenom?: string;
  email?: string;
  telephone?: string;
  date?: string;          // DD-MM-YYYY
  lieunaissance?: string;
  adress?: string;
  profil?: string;
  photo?: File;
}

// Interface pour la réponse paginée
export interface WorkersResponse {
  content: Worker[];
  pageable: Pageable;
  totalElements: number;
  totalPages: number;
  last: boolean;
  numberOfElements: number;
  size: number;
  number: number;
  sort: {
    unsorted: boolean;
    sorted: boolean;
    empty: boolean;
  };
  first: boolean;
  empty: boolean;
}

export interface CreateWorkerRequest {
  nom: string;
  prenom: string;
  email: string;
  password: string;
  telephone: string;
  date: string;
  lieunaissance: string;
  adress: string;
  profil: string;
  photo?: File; // Ajout du champ photo
}


@Injectable({
  providedIn: 'root'
})
export class UtilisateurService {

  private endpoints = environment.endpoints;


  constructor(
    private http: HttpClient,
    private authService: AuthService
  ) {}

  /**
   * Récupère la liste des utilisateurs/workers avec pagination
   * @param page Numéro de la page (par défaut 0)
   * @param size Taille de la page (par défaut 30)
   * @returns Observable de la réponse paginée
   */
  listUsers(page: number = 0, size: number = 30): Observable<WorkersResponse> {
    const currentUserId = this.authService.currentUser()?.id;
    
    if (!currentUserId) {
      throw new Error('Utilisateur non connecté');
    }

    const params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString());

    return this.http.get<WorkersResponse>(
      `${this.endpoints.workers.teamOthers.replace('{managerId}', currentUserId.toString())}`,

      {
        params,
        headers: this.getAuthHeaders()
      }
    );
  }
  getWorkers(page: number = 0, size: number = 30,propertyId:number): Observable<WorkersResponse> {

    const params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString());

    return this.http.get<WorkersResponse>(
      `${this.endpoints.workers.byProperty.replace('{propertyId}', propertyId.toString())}`,

      {
        params,
        headers: this.getAuthHeaders()
      }
    );
  }

  /**
   * Récupère la liste des workers par propriété depuis l'API de pointage
   * @param propertyId ID de la propriété
   * @param page Numéro de la page (par défaut 0)
   * @param size Taille de la page (par défaut 10)
   * @returns Observable de la réponse paginée
   */
  getWorkersByProperty(propertyId: number, page: number = 0, size: number = 10): Observable<WorkersResponse> {
    const params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString());

    const headers = new HttpHeaders({
      'accept': '*/*'
    });

    return this.http.get<WorkersResponse>(
      `${this.endpoints.workers.byProperty.replace('{propertyId}', propertyId.toString())}`,
      {
        params,
        headers
      }
    );
  }

  /**
   * Récupère tous les workers sans filtre de propriété
   * Utilise l'endpoint de recherche utilisateur
   * @param page Numéro de la page (par défaut 0)
   * @param size Taille de la page (par défaut 10)
   * @param search Terme de recherche optionnel (nom/prénom)
   * @returns Observable de la réponse paginée
   */
  getAllWorkers(page: number = 0, size: number = 10, search: string = ''): Observable<WorkersResponse> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString());

    if (search && search.trim()) {
      params = params.set('search', search.trim());
    }

    const headers = this.getAuthHeaders();

    return this.http.get<WorkersResponse>(
      `${environment.endpoints.user.search}`,
      {
        params,
        headers
      }
    );
  }

  searchWorkersByProperty(propertyId: number, search: string = '', page: number = 0, size: number = 10): Observable<WorkersResponse> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString());

    if (search && search.trim()) {
      params = params.set('search', search.trim());
    }

    const headers = new HttpHeaders({
      'accept': '*/*'
    });

    return this.http.get<WorkersResponse>(
      `${this.endpoints.workers.byProperty.replace('{propertyId}', propertyId.toString())}`,
      {
        params,
        headers
      }
    );
  }

  /**
   * Met à jour un utilisateur
   * @param userId ID de l'utilisateur
   * @param userData Données de l'utilisateur à mettre à jour
   * @returns Observable de l'utilisateur mis à jour
   */
  /** GET /api/v1/user/{id} — données complètes d'un utilisateur */
  getUserById(userId: number): Observable<Worker> {
    const token = this.authService.getToken();
    const headers = new HttpHeaders({ 'Authorization': `Bearer ${token}` });
    return this.http.get<Worker>(
      this.endpoints.user.byId.replace('{id}', userId.toString()),
      { headers }
    );
  }

  /** PUT /api/v1/user/update/{id} — multipart/form-data */
  updateUser(userId: number, data: UpdateWorkerFormData): Observable<Worker> {
    const formData = new FormData();
    if (data.nom?.trim())          formData.append('nom',           data.nom.trim());
    if (data.prenom?.trim())       formData.append('prenom',        data.prenom.trim());
    if (data.email?.trim())        formData.append('email',         data.email.trim());
    if (data.telephone?.trim())    formData.append('telephone',     data.telephone.trim());
    if (data.adress?.trim())       formData.append('adress',        data.adress.trim());
    if (data.profil?.trim())       formData.append('profil',        data.profil.trim());
    if (data.date?.trim())         formData.append('date',          data.date.trim());
    if (data.lieunaissance?.trim()) formData.append('lieunaissance', data.lieunaissance.trim());
    if (data.photo)                formData.append('photo',         data.photo, data.photo.name);

    const headers = new HttpHeaders({
      'Authorization': `Bearer ${this.authService.getToken()}`
    });

    return this.http.put<Worker>(
      this.endpoints.user.update.replace('{id}', userId.toString()),
      formData,
      { headers }
    );
  }

  /**
   * Supprime un utilisateur
   * @param userId ID de l'utilisateur
   * @param force Si true, supprime même s'il y a des données liées (orphelinage)
   * @returns Observable<void>
   */
  deleteUser(userId: number, force: boolean = false): Observable<void> {
    if (!userId || userId <= 0) {
      return throwError(() => ({
        message: 'ID utilisateur invalide',
        userMessage: 'L\'ID de l\'utilisateur doit être valide',
        status: 400
      }));
    }

    // ✅ Construire l'URL
    let deleteUrl = this.endpoints.user.delete.replace('{id}', userId.toString());
    
    // ✅ Ajouter le paramètre force si demandé
    if (force) {
      deleteUrl += '?force=true';
    }

    // ✅ Construire les headers avec le token directement
    const token = this.authService?.getToken() || localStorage.getItem('token');
    
    if (!token) {
      console.error('❌ Aucun token trouvé');
      return throwError(() => ({
        message: 'Non authentifié',
        userMessage: 'Veuillez vous reconnecter',
        status: 401
      }));
    }

    const headers = new HttpHeaders({
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    });

    console.log('📡 API Call: deleteUser');
    console.log('🔗 URL:', deleteUrl);
    console.log('👤 User ID:', userId);
    console.log('⚡ Mode force:', force);
    console.log('🔐 Token présent:', !!token);

    return this.http.delete<void>(deleteUrl, { headers })
      .pipe(
        tap(() => {
          console.log('✅ Utilisateur supprimé avec succès');
        }),
        catchError(error => {
          console.error('❌ Erreur lors de la suppression:', error);
          console.error('  - Status:', error.status);
          console.error('  - Message:', error.message);
          console.error('  - Body:', error.error);
          return throwError(() => error);
        })
      );
  }

  /**
   * Supprime un utilisateur avec gestion automatique des données liées
   * Essaie une suppression normale, puis force si erreur 409 (contrainte FK)
   * @param userId ID de l'utilisateur
   * @returns Observable<void>
   */
  deleteUserSmart(userId: number): Observable<void> {
    if (!userId || userId <= 0) {
      return throwError(() => ({
        message: 'ID utilisateur invalide',
        userMessage: 'L\'ID de l\'utilisateur doit être valide',
        status: 400
      }));
    }

    console.log('📡 API Call: deleteUserSmart - Tentative 1 (normal)');
    console.log('👤 User ID:', userId);

    // ✅ Essayer d'abord une suppression normale
    return this.deleteUser(userId, false).pipe(
      catchError(error => {
        // ✅ Si erreur 409 (Conflict - données liées), essayer en mode force
        if (error?.status === 409) {
          console.log('⚠️ Données liées détectées (409), tentative 2 (force=true)...');
          return this.deleteUser(userId, true).pipe(
            catchError(forceError => {
              console.error('❌ Erreur même en mode force:', forceError);
              return throwError(() => forceError);
            })
          );
        }

        // Sinon, propager l'erreur immédiatement
        console.log('❌ Erreur (non 409), pas de retry:', error?.status);
        return throwError(() => error);
      })
    );
  }

/**
 * Active ou désactive un utilisateur
 * @param userId ID de l'utilisateur
 * @param activated true pour activer, false pour désactiver
 * @returns Observable<void>
 */
blockUser(userId: number, activated: boolean): Observable<void> {
  const params = new HttpParams()
    .set('activated', activated.toString());

  return this.http.patch<void>(
    `${this.endpoints.user.update.replace('{id}', userId.toString())}/activation`,
    null, // Pas de body pour cette requête
    {
      params,
      headers: this.getAuthHeaders()
    }
  );
}
  /**
   * Récupère la liste des fournisseurs avec pagination
   * @param page Numéro de la page (par défaut 0)
   * @param size Taille de la page (par défaut 30)
   * @returns Observable de la réponse paginée des fournisseurs
   */
  getSuppliers(page: number = 0, size: number = 30): Observable<WorkersResponse> {
    const currentUserId = this.authService.currentUser()?.id;
    
    if (!currentUserId) {
      throw new Error('Utilisateur non connecté');
    }

    const params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString());

    return this.http.get<WorkersResponse>(
      `${this.endpoints.workers.suppliers.replace('{managerId}', currentUserId.toString())}`,
      { 
        params,
        headers: this.getAuthHeaders()
      }
    );
  }



  /**
   * Récupère la liste des sous-traitants avec pagination
   * @param page Numéro de la page (par défaut 0)
   * @param size Taille de la page (par défaut 30)
   * @returns Observable de la réponse paginée des sous-traitants
   */
  getSubcontractors(page: number = 0, size: number = 30): Observable<WorkersResponse> {
    const currentUserId = this.authService.currentUser()?.id;
    
    if (!currentUserId) {
      throw new Error('Utilisateur non connecté');
    }

    const params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString());

    return this.http.get<WorkersResponse>(
      `${this.endpoints.workers.subcontractors.replace('{managerId}', currentUserId.toString())}`,
      { 
        params,
        headers: this.getAuthHeaders()
      }
    );
  }

  /**
   * Crée un nouveau worker
   * @param workerData Données du worker à créer
   * @returns Observable de la réponse de création
   */ 
  createUser(workerData: CreateWorkerRequest): Observable<Worker> {
    const currentUserId = this.authService.currentUser()?.id;

    if (!currentUserId) {
      throw new Error('Utilisateur non connecté');
    }

    return this.http.post<Worker>(
      `${this.endpoints.workers.create.replace('{managerId}', currentUserId.toString())}`,
      workerData,
      { headers: this.getAuthHeaders() }
    );
  }


  createWorker(workerData: CreateWorkerRequest, propertyId: number): Observable<Worker> {
    if (!propertyId) {
      throw new Error('propertyId est requis');
    }
  
    // Créer un FormData pour envoyer le fichier
    const formData = new FormData();
    formData.append('nom', workerData.nom);
    formData.append('prenom', workerData.prenom);
    formData.append('email', workerData.email);
    formData.append('password', workerData.password);
    formData.append('telephone', workerData.telephone);
    formData.append('date', workerData.date);
    formData.append('lieunaissance', workerData.lieunaissance);
    formData.append('adress', workerData.adress);
    formData.append('profil', workerData.profil);
    
    // Ajouter la photo si elle existe
    if (workerData.photo) {
      formData.append('photo', workerData.photo, workerData.photo.name);
    }
  
    // Ne pas ajouter Content-Type dans les headers, le navigateur le fera automatiquement pour FormData
    const headers = new HttpHeaders({
      'Authorization': `Bearer ${this.authService.getToken()}`
    });
    
  
    return this.http.post<Worker>(
      `${this.endpoints.workers.save.replace('{propertyId}', propertyId.toString())}`,
      formData,
      { headers }
    );
  }
  
  /**
   * Crée un nouveau fournisseur (worker avec profil SUPPLIER)
   * @param supplierData Données du fournisseur à créer
   * @returns Observable de la réponse de création
   */
  createSupplier(supplierData: Omit<CreateWorkerRequest, 'profil'>): Observable<Worker> {
    const currentUserId = this.authService.currentUser()?.id;

    if (!currentUserId) {
      throw new Error('Utilisateur non connecté');
    }

    // Ajouter le profil SUPPLIER automatiquement
    const workerData: CreateWorkerRequest = {
      ...supplierData,
      profil: 'SUPPLIER'
    };

    return this.http.post<Worker>(
      `${this.endpoints.workers.create.replace('{managerId}', currentUserId.toString())}`,
      workerData,
      { headers: this.getAuthHeaders() }
    );
  }

  /**
   * Récupère les headers d'authentification
   * @returns HttpHeaders avec le token d'authentification
   */
  private getAuthHeaders(): HttpHeaders {
    const token = this.authService.getToken();
    return new HttpHeaders({
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    });
  }

  /**
   * Recherche des utilisateurs par nom
   * @param searchTerm Terme de recherche
   * @param page Numéro de la page
   * @param size Taille de la page
   * @returns Observable de la réponse paginée filtrée
   */
  searchUsers(searchTerm: string, page: number = 0, size: number = 30): Observable<WorkersResponse> {
    // Pour l'instant, on récupère tous les utilisateurs et on filtre côté client
    // Idéalement, l'API devrait supporter un paramètre de recherche
    return this.listUsers(page, size);
  }

  /**
   * Filtre les utilisateurs par statut
   * @param status Statut à filtrer
   * @param page Numéro de la page
   * @param size Taille de la page
   * @returns Observable de la réponse paginée filtrée
   */
  filterUsersByStatus(status: string, page: number = 0, size: number = 30): Observable<WorkersResponse> {
    // Pour l'instant, on récupère tous les utilisateurs et on filtre côté client
    // Idéalement, l'API devrait supporter un paramètre de filtrage par statut
    return this.listUsers(page, size);
  }

  /**
   * Convertit un Worker en TeamMember pour la compatibilité avec le composant existant
   * @param worker Worker à convertir
   * @returns TeamMember converti
   */
  static workerToTeamMember(worker: Worker): any {
    // Mapping des statuts - vous pouvez ajuster selon vos besoins
    let status: 'affecté' | 'non-affecté' | 'en mission' | 'inactive' = 'non-affecté';
    
    if (!worker.enabled || !worker.activated) {
      status = 'inactive';
    } else if (worker.profil === 'WORKER') {
      status = 'affecté'; // Par défaut, considérer les workers comme affectés
    }

    return {
      id: worker.id,
      name: `${worker.prenom} ${worker.nom}`,
      phone: worker.telephone,
      email: worker.email,
      position: worker.profil === 'WORKER' ? 'Ouvrier' : worker.profil,
      status: status,
      selected: false,
      // Données supplémentaires du worker
      originalWorker: worker
    };
  }

  /**
   * Convertit un Worker en Supplier pour l'affichage
   * @param worker Worker à convertir
   * @returns Supplier converti
   */
   workerToSupplier(worker: Worker): any {
    let status: 'actif' | 'inactif' = 'actif';
    
    if (!worker.enabled || !worker.activated) {
      status = 'inactif';
    }

    return {
      id: worker.id,
      raisonSociale: worker.company?.name || 'N/A',
      nomContact: `${worker.prenom} ${worker.nom}`,
      telephone: worker.telephone,
      email: worker.email,
      profil: worker.profil,
      status: status,
      selected: false,
      // Données supplémentaires du worker
      originalWorker: worker
    };
  }

  /**
   * Convertit un Worker en Subcontractor pour l'affichage
   * @param worker Worker à convertir
   * @returns Subcontractor converti
   */
  static workerToSubcontractor(worker: Worker): any {
    return {
      id: worker.id,
      raisonSociale: worker.company?.name || 'N/A',
      nomContact: `${worker.prenom} ${worker.nom}`,
      telephone: worker.telephone,
      email: worker.email,
      status: worker.enabled && worker.activated ? 'active' : 'inactive',
      selected: false,
      // Données supplémentaires du worker
      originalWorker: worker
    };
  }

  /**
   * Formate la date de création d'un worker
   * @param createdAt Date de création (string ou array)
   * @returns Date formatée
   */
  static formatCreatedDate(createdAt: string | number[]): Date {
    if (typeof createdAt === 'string') {
      return new Date(createdAt);
    }
    
    if (!createdAt || createdAt.length < 3) {
      return new Date();
    }
    
    const [year, month, day, hour = 0, minute = 0, second = 0] = createdAt;
    return new Date(year, month - 1, day, hour, minute, second);
  }

  /**
   * Valide les données de création d'un worker
   * @param workerData Données à valider
   * @returns Array des erreurs de validation
   */
  static validateWorkerData(workerData: CreateWorkerRequest): string[] {
    const errors: string[] = [];

    if (!workerData.nom?.trim()) {
      errors.push('Le nom est requis');
    }

    if (!workerData.prenom?.trim()) {
      errors.push('Le prénom est requis');
    }

    if (!workerData.email?.trim()) {
      errors.push('L\'email est requis');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(workerData.email)) {
      errors.push('Format d\'email invalide');
    }

    if (!workerData.password?.trim()) {
      errors.push('Le mot de passe est requis');
    } else if (workerData.password.length < 6) {
      errors.push('Le mot de passe doit contenir au moins 6 caractères');
    }

    if (!workerData.telephone?.trim()) {
      errors.push('Le téléphone est requis');
    }

    if (!workerData.adress?.trim()) {
      errors.push('L\'adresse est requise');
    }

    if (!workerData.lieunaissance?.trim()) {
      errors.push('Le lieu de naissance est requis');
    }

    if (!workerData.date?.trim()) {
      errors.push('La date de naissance est requise');
    }

    if (!workerData.profil?.trim()) {
      errors.push('Le profil est requis');
    }

    return errors;
  }

  /**
   * Valide les données de création d'un fournisseur
   * @param supplierData Données à valider
   * @returns Array des erreurs de validation
   */
  static validateSupplierData(supplierData: any): string[] {
    const errors: string[] = [];

    if (!supplierData.nom?.trim()) {
      errors.push('Le nom est requis');
    }

    if (!supplierData.prenom?.trim()) {
      errors.push('Le prénom est requis');
    }

    if (!supplierData.email?.trim()) {
      errors.push('L\'email est requis');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(supplierData.email)) {
      errors.push('Format d\'email invalide');
    }

    if (!supplierData.password?.trim()) {
      errors.push('Le mot de passe est requis');
    } else if (supplierData.password.length < 6) {
      errors.push('Le mot de passe doit contenir au moins 6 caractères');
    }

    if (!supplierData.telephone?.trim()) {
      errors.push('Le téléphone est requis');
    }

    if (!supplierData.adress?.trim()) {
      errors.push('L\'adresse est requise');
    }

    if (!supplierData.lieunaissance?.trim()) {
      errors.push('Le lieu de naissance est requis');
    }

    if (!supplierData.date?.trim()) {
      errors.push('La date de naissance est requise');
    }

    return errors;
  }
}
/*


*/